import React from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ReplayIcon from "@mui/icons-material/Replay";
import { useTutorial } from "./TutorialContext";

interface TutorialTooltipProps {
  targetRect: {
    top: number;
    left: number;
    width: number;
    height: number;
  };
}

export const TutorialTooltip: React.FC<TutorialTooltipProps> = ({
  targetRect,
}) => {
  const {
    currentStepIndex,
    steps,
    next,
    prev,
    closeTutorial,
    restart,
  } = useTutorial();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const currentStep = steps[currentStepIndex];
  const isLast = currentStepIndex === steps.length - 1;
  const isFirst = currentStepIndex === 0;

  const getPosition = () => {
    const spacing = 16;
    const tooltipWidth = isMobile ? window.innerWidth - 32 : 360;
    const tooltipHeight = 220;
    const padding = 16;

    const clamp = (value: number, min: number, max: number) =>
      Math.max(min, Math.min(value, max));

    if (!targetRect.width && !targetRect.height) {
      return {
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
      };
    }

    // Available space on each side of the target
    const spaceTop = targetRect.top;
    const spaceBottom = window.innerHeight - (targetRect.top + targetRect.height);
    const spaceLeft = targetRect.left;
    const spaceRight = window.innerWidth - (targetRect.left + targetRect.width);
    const requiredV = tooltipHeight + spacing + padding;
    const requiredH = tooltipWidth + spacing + padding;

    // Auto-flip when the requested side does not have enough room
    let effectivePosition = currentStep.position ?? "center";
    if (effectivePosition === "top" && spaceTop < requiredV && spaceBottom >= requiredV) {
      effectivePosition = "bottom";
    } else if (effectivePosition === "bottom" && spaceBottom < requiredV && spaceTop >= requiredV) {
      effectivePosition = "top";
    } else if (effectivePosition === "left" && spaceLeft < requiredH && spaceRight >= requiredH) {
      effectivePosition = "right";
    } else if (effectivePosition === "right" && spaceRight < requiredH && spaceLeft >= requiredH) {
      effectivePosition = "left";
    }

    let top = 0;
    let left = 0;
    let transform = "";

    // Compute a plain (non-transformed) top/left representing the tooltip's
    // top-left corner, so we can clamp against the viewport reliably.
    switch (effectivePosition) {
      case "left":
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left - tooltipWidth - spacing;
        break;
      case "right":
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left + targetRect.width + spacing;
        break;
      case "top":
        top = targetRect.top - tooltipHeight - spacing;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      case "bottom":
        top = targetRect.top + targetRect.height + spacing;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
      case "center":
      default:
        top = targetRect.top + targetRect.height / 2 - tooltipHeight / 2;
        left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;
        break;
    }

    left = clamp(left, padding, Math.max(padding, window.innerWidth - tooltipWidth - padding));
    top = clamp(top, padding, Math.max(padding, window.innerHeight - tooltipHeight - padding));

    // Collision avoidance: after clamping, the tooltip may still visually
    // overlap the highlighted target (e.g. target near a viewport edge).
    // Nudge the tooltip perpendicular to the chosen side to clear the target.
    const overlapsH =
      left < targetRect.left + targetRect.width && left + tooltipWidth > targetRect.left;
    const overlapsV =
      top < targetRect.top + targetRect.height && top + tooltipHeight > targetRect.top;

    if (overlapsH && overlapsV) {
      if (effectivePosition === "top" || effectivePosition === "bottom") {
        // Push horizontally to whichever side has room
        const leftCandidate = targetRect.left - tooltipWidth - spacing;
        const rightCandidate = targetRect.left + targetRect.width + spacing;
        if (rightCandidate + tooltipWidth <= window.innerWidth - padding) {
          left = rightCandidate;
        } else if (leftCandidate >= padding) {
          left = leftCandidate;
        }
      } else {
        // left / right / center — push vertically to whichever side has room
        const topCandidate = targetRect.top - tooltipHeight - spacing;
        const bottomCandidate = targetRect.top + targetRect.height + spacing;
        if (bottomCandidate + tooltipHeight <= window.innerHeight - padding) {
          top = bottomCandidate;
        } else if (topCandidate >= padding) {
          top = topCandidate;
        }
      }

      left = clamp(left, padding, Math.max(padding, window.innerWidth - tooltipWidth - padding));
      top = clamp(top, padding, Math.max(padding, window.innerHeight - tooltipHeight - padding));
    }

    return { top, left, transform };
  };

  const position = getPosition();

  return (
    <Paper
      elevation={6}
      sx={{
        position: "absolute",
        ...position,
        width: isMobile ? "calc(100% - 32px)" : 360,
        maxWidth: "100vw",
        p: 3,
        borderRadius: 3,
        bgcolor: "#FFFFFF",
        zIndex: (theme) => theme.zIndex.modal + 2,
        transition: "top 0.3s ease, left 0.3s ease, transform 0.3s ease",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, pr: 2 }}>
          {currentStep.title}
        </Typography>
        <IconButton size="small" onClick={closeTutorial} aria-label="close tutorial">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
        {currentStep.description}
      </Typography>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<ReplayIcon />}
          onClick={restart}
          disabled={steps.length <= 1}
        >
          Restart
        </Button>

        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={prev}
            disabled={isFirst}
          >
            Back
          </Button>
          <Button
            variant="contained"
            size="small"
            endIcon={<ArrowForwardIcon />}
            onClick={next}
            sx={{
              bgcolor: "#EE2D3D",
              "&:hover": { bgcolor: "#d42635" },
            }}
          >
            {isLast ? "Finish" : "Next"}
          </Button>
        </Box>
      </Box>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          mt: 2,
          color: "text.secondary",
        }}
      >
        Step {currentStepIndex + 1} of {steps.length}
      </Typography>
    </Paper>
  );
};

export default TutorialTooltip;
